/**
 * TrustGrid Dashboard - Turning invisible risks into visible, actionable insights
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Tag, Spin, Row, Col, Card, Tooltip, Button, Modal, Checkbox } from 'antd';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { PlayCircleOutlined, RiseOutlined, FallOutlined, QuestionCircleOutlined, DownloadOutlined, BarChartOutlined, SettingOutlined, EyeOutlined } from '@ant-design/icons';
import { getLatestScan, runScan, getScanSignals, getProject, getControl, listControls, getControlResults } from '../api/trustgridApi';
import { getTrustScoreTextColor } from '../api/trustgridApi';
import { useTrustGrid } from '../../../contexts/TrustGridContext';
import { showError, showSuccess } from '../../../utils/notifications';
import TrustGridPage from '../../../components/layout/TrustGridPage';
import ProjectSelector from './ProjectSelector';
import EvidenceModal from './EvidenceModal';
import TrustScoreTrend from './TrustScoreTrend';
import ScoreBreakdownModal from './ScoreBreakdownModal';
import FrameworkDetailModal from './FrameworkDetailModal';
import ThreeScoreGauges from './ThreeScoreGauges';
import ScoreTrendChart from './ScoreTrendChart';

function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedProjectId, isAllProjects, selectProject } = useTrustGrid();
  const [projectsForSelection, setProjectsForSelection] = useState([]);

  // If "All Projects" selected when landing on Dashboard, auto-select first project
  // Dashboard requires single project context, can't show "All Projects" view
  useEffect(() => {
    if (isAllProjects) {
      // Fetch projects to auto-select first one
      const fetchAndSelectFirst = async () => {
        try {
          const { listProjects } = await import('../api/trustgridApi');
          const response = await listProjects();
          const projects = response.data.projects || [];
          if (projects.length > 0) {
            // Auto-select first project
            selectProject(projects[0].id, projects[0]);
          } else {
            // No projects available, redirect to projects page
            navigate('/trustgrid/projects');
          }
        } catch (error) {
          console.error('Failed to fetch projects for auto-selection:', error);
          navigate('/trustgrid/projects');
        }
      };
      fetchAndSelectFirst();
    }
  }, [isAllProjects, navigate, selectProject]);

  const projectId = selectedProjectId;

  // Build breadcrumbs from navigation state or use defaults
  const breadcrumbs = useMemo(() => {
    // If coming from another page (e.g., Projects), use those breadcrumbs as base
    // Otherwise start with just TrustGrid
    const baseBreadcrumbs = location.state?.breadcrumbs || [];

    // Always add Dashboard to breadcrumbs
    // If no base breadcrumbs, this creates: [Dashboard]
    // If from Projects, this creates: [TrustGrid, Projects, Dashboard]
    return [...baseBreadcrumbs, { label: 'Dashboard', path: '/trustgrid/dashboard' }];
  }, [location.state]);
  const [project, setProject] = useState(null);
  const [scan, setScan] = useState(null);
  const [signals, setSignals] = useState([]);
  const [controls, setControls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [evidenceModalVisible, setEvidenceModalVisible] = useState(false);
  const [selectedSignal, setSelectedSignal] = useState(null);
  const [selectedControl, setSelectedControl] = useState(null);
  const [breakdownModalVisible, setBreakdownModalVisible] = useState(false);
  const [frameworkModalVisible, setFrameworkModalVisible] = useState(false);
  const [selectedFramework, setSelectedFramework] = useState(null);
  const [showAllFrameworks, setShowAllFrameworks] = useState(false);
  const [pinnedFrameworks, setPinnedFrameworks] = useState(() => {
    const saved = localStorage.getItem(`pinnedFrameworks_${projectId}`);
    return saved ? JSON.parse(saved) : [];
  });
  const [configModalVisible, setConfigModalVisible] = useState(false);

  useEffect(() => {
    // Only fetch if we have a valid project ID (number, not 'all' or null)
    if (projectId && typeof projectId === 'number') {
      fetchData();
      // Poll every 10s if scan is running
      const interval = setInterval(() => {
        if (scan && scan.status === 'running') {
          fetchData();
        }
      }, 10000);

      return () => clearInterval(interval);
    }
  }, [projectId]);

  // Save pinned frameworks to localStorage
  useEffect(() => {
    localStorage.setItem(`pinnedFrameworks_${projectId}`, JSON.stringify(pinnedFrameworks));
  }, [pinnedFrameworks, projectId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [projRes, scanRes, controlsRes, resultsRes] = await Promise.all([
        getProject(projectId).catch(err => {
          // SECURITY: Silently handle 404 for cross-org projects
          // Don't show error messages to user about projects they shouldn't know about
          if (err.response?.status === 404) {
            console.warn('Project not accessible (likely cross-org)');
            return null;
          }
          throw err;
        }),
        getLatestScan(projectId).catch(() => ({ data: null })),
        listControls(),
        getControlResults(projectId).catch(() => ({ data: { results: [] } }))
      ]);

      // If project not accessible (cross-org), clear selection and navigate to projects
      if (!projRes) {
        console.warn('[Dashboard] Project not accessible, clearing selection');
        selectProject(null);  // Clear invalid project from context
        navigate('/trustgrid/projects');
        return;
      }

      setProject(projRes.data);

      // Enrich controls with results data and regulations
      const controlsData = controlsRes.data.controls || [];
      const resultsMap = {};
      (resultsRes.data.results || []).forEach(r => {
        resultsMap[r.control_code] = r;
      });

      const enrichedControls = controlsData.map(c => ({
        ...c,
        status: resultsMap[c.code]?.status || 'unknown',
        confidence: resultsMap[c.code]?.confidence || 0,
        regulations: extractRegulations(c.regulation_data)
      }));

      setControls(enrichedControls);

      if (scanRes.data) {
        setScan(scanRes.data);
        if (scanRes.data.id) {
          const signalsRes = await getScanSignals(scanRes.data.id);
          setSignals(signalsRes.data.signals || []);
        }
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  // Extract frameworks from regulation_data (same logic as ComplianceDashboard)
  const extractRegulations = (regulationData) => {
    if (!regulationData || !regulationData.regulations) return [];
    const frameworks = new Set();
    regulationData.regulations.forEach(reg => {
      if (reg.framework) {
        frameworks.add(reg.framework);
      }
    });
    return Array.from(frameworks);
  };

  const handleRunScan = async () => {
    setScanning(true);
    try {
      await runScan(projectId);
      showSuccess('Scan started successfully');
      setTimeout(() => fetchData(), 2000);
    } catch (error) {
      console.error('Failed to run scan:', error);
      showError(error);
    } finally {
      setScanning(false);
    }
  };

  const trustScore = scan?.trust_score || 0;
  const scoreColor = getTrustScoreTextColor(trustScore);

  // Gauge data
  const gaugeData = [
    { value: trustScore },
    { value: 100 - trustScore }
  ];

  const COLORS = [scoreColor, '#e0e0e0'];

  // Get failing controls - sort by severity before limiting to 3
  const allFailingControls = signals.filter(s => s.status === 'fail');
  const severityOrder = { 'critical': 0, 'high': 1, 'medium': 2, 'low': 3, 'unknown': 4 };
  const sortedFailingControls = allFailingControls.sort((a, b) => {
    const aSeverity = controls.find(c => c.code === a.control_code)?.severity || 'unknown';
    const bSeverity = controls.find(c => c.code === b.control_code)?.severity || 'unknown';
    return severityOrder[aSeverity] - severityOrder[bSeverity];
  });
  const failingControls = sortedFailingControls.slice(0, 3);

  // Dynamically extract all unique frameworks from scanned controls (same as ComplianceDashboard)
  const scannedControls = controls.filter(c => c.status !== 'unknown');
  console.log('[DEBUG] Total controls:', controls.length);
  console.log('[DEBUG] Scanned controls:', scannedControls.length);

  const allFrameworks = new Set();
  scannedControls.forEach(c => {
    if (c.regulations && Array.isArray(c.regulations)) {
      c.regulations.forEach(framework => allFrameworks.add(framework));
    }
  });

  console.log('[DEBUG] Extracted frameworks from controls:', Array.from(allFrameworks));
  console.log('[DEBUG] Scan framework_scores:', scan?.framework_scores);

  // Build framework scores: use scan.framework_scores for actual scores, 0 for frameworks not in scan
  const scanFrameworkScores = scan?.framework_scores || {};
  const frameworkScores = {};
  Array.from(allFrameworks).forEach(framework => {
    frameworkScores[framework] = scanFrameworkScores[framework] !== undefined
      ? scanFrameworkScores[framework]
      : 0;  // Framework exists in controls but not scanned yet
  });

  console.log('[DEBUG] Final frameworkScores object:', frameworkScores);

  // Get frameworks to display
  const allFrameworksList = Object.entries(frameworkScores);
  const displayedFrameworks = (() => {
    if (showAllFrameworks || allFrameworksList.length === 0) {
      return allFrameworksList;
    }

    // If user has pinned frameworks, show those (max 5)
    if (pinnedFrameworks.length > 0) {
      return allFrameworksList
        .filter(([framework]) => pinnedFrameworks.includes(framework))
        .slice(0, 5);
    }

    // Otherwise show top 5 by score
    return allFrameworksList
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5);
  })();

  const handleToggleFramework = (framework) => {
    setPinnedFrameworks(prev =>
      prev.includes(framework)
        ? prev.filter(f => f !== framework)
        : [...prev, framework].slice(0, 5)  // Max 5 pinned
    );
  };

  // Helper function to open evidence modal
  const handleViewEvidence = async (signal) => {
    try {
      const controlRes = await getControl(signal.control_code);
      setSelectedControl(controlRes.data);
      setSelectedSignal(signal);
      setEvidenceModalVisible(true);
    } catch (error) {
      console.error('Failed to load control data:', error);
      setSelectedSignal(signal);
      setEvidenceModalVisible(true);
    }
  };

  return (
    <TrustGridPage
      title={project?.name || 'Dashboard'}
      subtitle="Turning invisible risks into visible, actionable insights"
      breadcrumbs={breadcrumbs}
      showRefresh
      onRefresh={fetchData}
      actions={
        <Button
          type="primary"
          icon={<PlayCircleOutlined />}
          onClick={handleRunScan}
          loading={scanning}
          disabled={loading}
        >
          Run Scan
        </Button>
      }
    >
      <ProjectSelector />

      {loading && !scan ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      ) : !scan ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <h3>No Scans Yet</h3>
            <p style={{ color: '#888', marginBottom: '20px' }}>
              Run your first scan to see compliance metrics
            </p>
            <Button type="primary" icon={<PlayCircleOutlined />} onClick={handleRunScan}>
              Run First Scan
            </Button>
          </div>
        </Card>
      ) : (
        <Row gutter={[16, 16]}>
          {/* Trust Score Gauge */}
          <Col xs={24} md={12}>
            <Card>
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <h3>
                  Trust Score{' '}
                  <Tooltip title={
                    <div>
                      <p><strong>How Trust Score Works:</strong></p>
                      <p>Start at 100 points. Deductions based on severity:</p>
                      <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
                        <li><strong>Critical:</strong> -10 pts each (max -40 pts pool)</li>
                        <li><strong>High:</strong> -7 pts each (max -35 pts pool)</li>
                        <li><strong>Medium:</strong> -4 pts each (max -25 pts pool)</li>
                        <li><strong>Low:</strong> -2 pts each</li>
                      </ul>
                      <p style={{ marginTop: 8, marginBottom: 0 }}>
                        <em>Confidence factor adjusts deductions.</em>
                      </p>
                    </div>
                  } placement="right">
                    <QuestionCircleOutlined style={{ fontSize: 14, color: '#888', cursor: 'help' }} />
                  </Tooltip>
                </h3>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={gaugeData}
                      cx="50%"
                      cy="50%"
                      startAngle={180}
                      endAngle={0}
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={0}
                      dataKey="value"
                    >
                      {gaugeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index]} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <h1 style={{ color: scoreColor, fontWeight: 'bold', marginTop: '-64px' }}>
                  {trustScore}
                </h1>
                <div style={{ marginTop: '16px' }}>
                  {trustScore >= 80 ? (
                    <Tag icon={<RiseOutlined />} color="success">Good Standing</Tag>
                  ) : trustScore >= 60 ? (
                    <Tag color="warning">Needs Attention</Tag>
                  ) : (
                    <Tag icon={<FallOutlined />} color="error">Critical Issues</Tag>
                  )}
                </div>
              </div>
            </Card>
          </Col>

          {/* Framework Scores */}
          <Col xs={24} md={12}>
            <Card>
              <div style={{ padding: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h3 style={{ margin: 0 }}>
                    Framework Compliance{' '}
                    <Tooltip title="Scores calculated dynamically from control regulations. Each framework evaluates only its assigned controls." placement="right">
                      <QuestionCircleOutlined style={{ fontSize: 14, color: '#888', cursor: 'help' }} />
                    </Tooltip>
                  </h3>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {allFrameworksList.length > 0 && (
                      <>
                        <Button
                          size="small"
                          icon={<SettingOutlined />}
                          onClick={() => setConfigModalVisible(true)}
                        >
                          Configure
                        </Button>
                        {allFrameworksList.length > 5 && (
                          <Button
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => setShowAllFrameworks(!showAllFrameworks)}
                          >
                            {showAllFrameworks ? 'Show Top 5' : `View All (${allFrameworksList.length})`}
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
                {allFrameworksList.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                    <p>No framework mappings found</p>
                    <p style={{ fontSize: '12px' }}>Assign controls with regulations to see framework scores</p>
                  </div>
                ) : displayedFrameworks.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#888' }}>
                    <p>No pinned frameworks found in current scan</p>
                    <Button size="small" onClick={() => setConfigModalVisible(true)}>
                      Configure Frameworks
                    </Button>
                  </div>
                ) : (
                  displayedFrameworks.map(([framework, score]) => (
                    <div key={framework} style={{ marginBottom: '16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span>{framework}</span>
                        <span style={{ fontWeight: 'bold' }}>{score}%</span>
                      </div>
                      <div style={{ width: '100%', backgroundColor: '#e0e0e0', height: '8px', borderRadius: '4px' }}>
                        <div
                          style={{
                            width: `${score}%`,
                            backgroundColor: score >= 85 ? '#2e7d32' : score >= 70 ? '#ed6c02' : '#d32f2f',
                            height: '8px',
                            borderRadius: '4px'
                          }}
                        />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </Col>

          {/* Trust Score Trend */}
          <Col xs={24}>
            <TrustScoreTrend projectId={projectId} />
          </Col>

          {/* Three-Score System (Trust, Compliance, Maturity) */}
          <Col xs={24}>
            <ThreeScoreGauges projectId={projectId} />
          </Col>

          {/* Score History Trend */}
          <Col xs={24}>
            <ScoreTrendChart projectId={projectId} />
          </Col>

          {/* Top Failing Controls */}
          <Col xs={24}>
            <Card>
              <div style={{ padding: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <h3 style={{ margin: 0 }}>Top Risks (Critical & High Severity)</h3>
                  {allFailingControls.length > 3 && (
                    <Button
                      type="link"
                      onClick={() => navigate('/trustgrid/controls?status=fail')}
                      style={{ padding: 0 }}
                    >
                      View All {allFailingControls.length} Failures →
                    </Button>
                  )}
                </div>
                {scan && scan.started_at && (
                  <div style={{ marginBottom: '16px', color: '#666', fontSize: '13px' }}>
                    Last scanned: {new Date(scan.started_at).toLocaleString()} • Showing event timestamps below
                  </div>
                )}
                {failingControls.length === 0 ? (
                  <p style={{ color: '#888' }}>No failing controls</p>
                ) : (
                  <Row gutter={[16, 16]}>
                    {failingControls.map((signal) => (
                      <Col xs={24} md={8} key={signal.id}>
                        <Card style={{ backgroundColor: '#ffebee' }}>
                          <div style={{ padding: '12px' }}>
                            <h4 style={{ marginBottom: '8px' }}>{signal.control_code}</h4>
                            <p style={{ marginBottom: '12px', fontSize: '14px' }}>
                              {signal.rationale?.substring(0, 100)}...
                            </p>
                            <Button
                              size="small"
                              onClick={() => handleViewEvidence(signal)}
                            >
                              View Evidence
                            </Button>
                          </div>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                )}
              </div>
            </Card>
          </Col>

          {/* Quick Links */}
          <Col xs={24}>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <Button type="primary" onClick={() => navigate('/trustgrid/controls')}>
                View All Controls
              </Button>
              {scan && (
                <>
                  <Button icon={<BarChartOutlined />} onClick={() => setBreakdownModalVisible(true)}>
                    Score Breakdown
                  </Button>
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={() => window.open(`/api/trustgrid/v1/scans/${scan.id}/export/csv`, '_blank')}
                  >
                    Export CSV
                  </Button>
                </>
              )}
              <Button onClick={() => navigate('/trustgrid/evidence')}>
                Evidence Graph
              </Button>
            </div>
          </Col>
        </Row>
      )}

      {/* Evidence Modal */}
      {selectedSignal && (
        <EvidenceModal
          visible={evidenceModalVisible}
          onClose={() => {
            setEvidenceModalVisible(false);
            setSelectedSignal(null);
            setSelectedControl(null);
          }}
          signal={selectedSignal}
          controlData={selectedControl}
        />
      )}

      {/* Score Breakdown Modal */}
      {scan && (
        <ScoreBreakdownModal
          visible={breakdownModalVisible}
          onClose={() => setBreakdownModalVisible(false)}
          scanId={scan.id}
        />
      )}

      {/* Framework Detail Modal */}
      {scan && selectedFramework && (
        <FrameworkDetailModal
          visible={frameworkModalVisible}
          onClose={() => {
            setFrameworkModalVisible(false);
            setSelectedFramework(null);
          }}
          scanId={scan.id}
          frameworkName={selectedFramework}
        />
      )}

      {/* Framework Configuration Modal */}
      <Modal
        title="Configure Framework Dashboard"
        open={configModalVisible}
        onCancel={() => setConfigModalVisible(false)}
        onOk={() => setConfigModalVisible(false)}
        width={600}
      >
        <p style={{ marginBottom: '16px', color: '#666' }}>
          Select up to 5 frameworks to pin to your dashboard. If no frameworks are selected,
          the top 5 by score will be displayed automatically.
        </p>
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {allFrameworksList.length === 0 ? (
            <p style={{ color: '#888', textAlign: 'center', padding: '20px' }}>
              No frameworks available. Run a scan to see framework scores.
            </p>
          ) : (
            allFrameworksList
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([framework, score]) => (
                <div
                  key={framework}
                  style={{
                    padding: '12px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                    marginBottom: '8px',
                    cursor: 'pointer',
                    backgroundColor: pinnedFrameworks.includes(framework) ? '#e6f7ff' : 'white',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                  onClick={() => handleToggleFramework(framework)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    <Checkbox
                      checked={pinnedFrameworks.includes(framework)}
                      onChange={() => handleToggleFramework(framework)}
                      disabled={!pinnedFrameworks.includes(framework) && pinnedFrameworks.length >= 5}
                    />
                    <span style={{ fontWeight: pinnedFrameworks.includes(framework) ? 'bold' : 'normal' }}>
                      {framework}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ color: '#888' }}>{score}%</span>
                    <div style={{ width: '80px', backgroundColor: '#e0e0e0', height: '6px', borderRadius: '3px' }}>
                      <div
                        style={{
                          width: `${score}%`,
                          backgroundColor: score >= 85 ? '#2e7d32' : score >= 70 ? '#ed6c02' : '#d32f2f',
                          height: '6px',
                          borderRadius: '3px'
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))
          )}
        </div>
        {pinnedFrameworks.length >= 5 && (
          <div style={{ marginTop: '12px', padding: '8px', backgroundColor: '#fff7e6', borderRadius: '4px' }}>
            <span style={{ color: '#fa8c16' }}>
              Maximum 5 frameworks can be pinned. Uncheck one to select another.
            </span>
          </div>
        )}
        {pinnedFrameworks.length > 0 && (
          <div style={{ marginTop: '12px', padding: '8px', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
            <strong>Pinned ({pinnedFrameworks.length}/5):</strong> {pinnedFrameworks.join(', ')}
          </div>
        )}
      </Modal>
    </TrustGridPage>
  );
}

export default Dashboard;
